import { useRef, useState } from 'react';
import { ExternalLink, Link2, CirclePlay, Trash2, Upload } from 'lucide-react';
import { Carte, MessageErreur, Pastille } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  annoncerDepotReplay,
  dateCourte,
  dureeLisible,
  envoyerFichierReplay,
  tailleLisible,
  useGestesClasse,
  type DetailClasse,
  type LigneReplay,
} from '../../lib/useElearning';

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm focus:border-codialis-sky focus:outline-none';
const FORMATS_ACCEPTES = 'video/mp4,video/webm,.mp4,.m4v,.webm';

interface DepotEnCours {
  replayId: string;
  nom: string;
  recus: number;
  total: number;
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/**
 * Replays d'une classe (CDC §7.2) : l'enregistrement de la salle, un fichier
 * déposé — par morceaux, reprenable après une coupure —, ou un lien externe.
 * Les apprenants y figurent : chaque replay dit jusqu'à quand il est conservé.
 */
export function CarteReplays({
  classe,
  onLire,
}: {
  classe: DetailClasse;
  onLire: (replay: LigneReplay) => void;
}): JSX.Element {
  const toast = useToast();
  const gestes = useGestesClasse();
  const [titre, setTitre] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const [depot, setDepot] = useState<DepotEnCours | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [lien, setLien] = useState({ titre: '', url: '' });
  const [aRetirer, setARetirer] = useState<LigneReplay | null>(null);
  const [motif, setMotif] = useState('');
  const annulation = useRef<AbortController | null>(null);
  const reprise = useRef<HTMLInputElement>(null);
  const [aReprendre, setAReprendre] = useState<LigneReplay | null>(null);

  const replays = classe.replaysDetail;
  const visibles = replays.filter((replay) => replay.statut !== 'RETIRE');
  const retires = replays.filter((replay) => replay.statut === 'RETIRE');
  const peutDeposer = classe.refus.depot === null;
  const conservation = replays.find((replay) => replay.statut !== 'RETIRE')?.conserveJusquau ?? null;

  const envoyer = async (replayId: string, source: File) => {
    const controleur = new AbortController();
    annulation.current = controleur;
    setDepot({ replayId, nom: source.name, recus: 0, total: source.size });
    setErreur(null);
    try {
      await envoyerFichierReplay(
        replayId,
        source,
        (recus) => setDepot((actuel) => (actuel ? { ...actuel, recus } : actuel)),
        controleur.signal,
      );
      toast('Replay déposé · disponible pour les apprenants');
      setFichier(null);
      setTitre('');
    } catch (cause) {
      setErreur(
        controleur.signal.aborted
          ? 'Dépôt interrompu : choisissez à nouveau le même fichier pour reprendre où il en était.'
          : messageErreur(cause, 'Le dépôt a échoué : reprenez-le, il repartira du dernier morceau reçu.'),
      );
    } finally {
      annulation.current = null;
      setDepot(null);
      void gestes.relire();
    }
  };

  const deposer = async () => {
    if (!fichier) return;
    try {
      const replayId = await annoncerDepotReplay(classe.seanceId, titre.trim() || classe.libelle, fichier);
      await envoyer(replayId, fichier);
    } catch (cause) {
      setErreur(messageErreur(cause, 'Le dépôt n’a pas pu commencer.'));
    }
  };

  const reprendre = async (source: File) => {
    const ligne = aReprendre;
    setAReprendre(null);
    if (!ligne) return;
    if (source.size !== ligne.tailleAttendue) {
      setErreur(`Ce n’est pas le même fichier : « ${ligne.titre} » attend ${tailleLisible(ligne.tailleAttendue)}.`);
      return;
    }
    await envoyer(ligne.id, source);
  };

  const ajouterLien = async () => {
    setErreur(null);
    try {
      await gestes.lierReplay.mutateAsync({
        seanceId: classe.seanceId,
        titre: lien.titre.trim() || classe.libelle,
        url: lien.url.trim(),
      });
      setLien({ titre: '', url: '' });
      toast('Lien de replay ajouté');
    } catch (cause) {
      setErreur(messageErreur(cause, 'Le lien n’a pas pu être ajouté.'));
    }
  };

  const confirmerRetrait = async () => {
    if (!aRetirer) return;
    try {
      await gestes.retirerReplay.mutateAsync({ replayId: aRetirer.id, ...(motif.trim() ? { motif: motif.trim() } : {}) });
      toast('Replay retiré · fichier effacé');
      setARetirer(null);
      setMotif('');
    } catch (cause) {
      setErreur(messageErreur(cause, 'Le replay n’a pas pu être retiré.'));
      setARetirer(null);
    }
  };

  return (
    <Carte titre="Sessions archivées (replay)">
      {visibles.length === 0 && (
        <p className="px-4 py-4 text-sm text-codialis-gray sm:px-6">
          Aucun replay pour cette classe.{' '}
          {peutDeposer ? 'Enregistrez la salle pendant le direct, ou déposez le fichier ensuite.' : ''}
        </p>
      )}
      {visibles.map((replay) => {
        const enDepot = replay.statut === 'EN_COURS' && replay.origine === 'DEPOT';
        const progression =
          enDepot && replay.tailleAttendue ? Math.floor(((replay.recus ?? 0) / replay.tailleAttendue) * 100) : null;
        const details = [
          replay.origineLibelle,
          dateCourte(replay.disponibleLe ?? replay.creeLe),
          dureeLisible(replay.dureeSecondes),
          tailleLisible(replay.tailleOctets),
          replay.statut === 'DISPONIBLE' ? `${replay.vues} vue${replay.vues > 1 ? 's' : ''}` : null,
        ].filter(Boolean);
        return (
          <div key={replay.id} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            <div className="min-w-0 flex-1 basis-48">
              <div className="truncate text-sm font-medium text-codialis-navy">{replay.titre}</div>
              <div className="text-[11px] text-codialis-gray">{details.join(' · ')}</div>
              {progression !== null && depot?.replayId !== replay.id && (
                <div className="text-[11px] font-semibold text-amber-700">Dépôt interrompu à {progression} %</div>
              )}
              {replay.statut === 'ECHEC' && replay.motif && (
                <div className="text-[11px] text-red-700">{replay.motif}</div>
              )}
            </div>
            <Pastille texte={replay.statutLibelle} ton={replay.statutTon} />
            <div className="flex shrink-0 items-center gap-1">
              {replay.lecture && (
                <button
                  type="button"
                  onClick={() => onLire(replay)}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:bg-gray-50"
                >
                  <CirclePlay className="h-4 w-4" aria-hidden />
                  Replay
                </button>
              )}
              {replay.url && (
                <a
                  href={replay.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:bg-gray-50"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  Ouvrir
                </a>
              )}
              {enDepot && peutDeposer && depot === null && (
                <button
                  type="button"
                  onClick={() => {
                    setAReprendre(replay);
                    reprise.current?.click();
                  }}
                  className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-navy hover:bg-gray-50"
                >
                  <Upload className="h-4 w-4" aria-hidden />
                  Reprendre
                </button>
              )}
              {replay.refusRetrait === null && depot?.replayId !== replay.id && (
                <button
                  type="button"
                  onClick={() => setARetirer(replay)}
                  aria-label={`Retirer le replay ${replay.titre}`}
                  className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg text-codialis-gray hover:bg-gray-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
            </div>
          </div>
        );
      })}

      <input
        ref={reprise}
        type="file"
        accept={FORMATS_ACCEPTES}
        className="hidden"
        onChange={(evenement) => {
          const choisi = evenement.target.files?.[0];
          evenement.target.value = '';
          if (choisi) void reprendre(choisi);
        }}
      />

      {depot && (
        <div className="px-4 py-3 sm:px-6" role="status">
          <div className="flex items-center justify-between gap-3 text-xs text-codialis-navy">
            <span className="min-w-0 truncate">Dépôt de « {depot.nom} »</span>
            <span className="shrink-0 font-mono">{Math.floor((depot.recus / depot.total) * 100)} %</span>
          </div>
          <div className="progress-track mt-2 h-1.5 rounded-full">
            <div className="progress-bar h-full rounded-full" style={{ width: `${(depot.recus / depot.total) * 100}%` }} />
          </div>
          <button
            type="button"
            onClick={() => annulation.current?.abort()}
            className="mt-2 min-h-[36px] text-xs font-semibold text-codialis-gray hover:text-codialis-navy"
          >
            Interrompre
          </button>
        </div>
      )}

      {erreur && (
        <div className="px-4 py-3 sm:px-6">
          <MessageErreur message={erreur} />
        </div>
      )}

      {peutDeposer && depot === null && (
        <div className="space-y-3 px-4 py-4 sm:px-6">
          <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Déposer un enregistrement</div>
          <input
            type="text"
            value={titre}
            onChange={(evenement) => setTitre(evenement.target.value)}
            placeholder={classe.libelle}
            aria-label="Titre du replay"
            className={CLASSE_CHAMP}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            {/* Le champ natif s'affiche dans la langue du navigateur : un
                libellé à la charte le remplace, le champ reste accessible. */}
            <label className="relative flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 text-sm text-codialis-navy hover:bg-gray-100 focus-within:ring-2 focus-within:ring-codialis-sky">
              <input
                type="file"
                accept={FORMATS_ACCEPTES}
                aria-label="Fichier vidéo (MP4 ou WebM, 4 Go au plus)"
                onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
                className="sr-only"
              />
              <Upload className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden />
              <span className="min-w-0 truncate">
                {fichier ? `${fichier.name} · ${tailleLisible(fichier.size)}` : 'Choisir une vidéo'}
              </span>
            </label>
            <button
              type="button"
              disabled={!fichier}
              onClick={() => void deposer()}
              className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
            >
              <Upload className="h-4 w-4" aria-hidden />
              Déposer
            </button>
          </div>
          <p className="text-[11px] text-codialis-gray">MP4 ou WebM, 4 Go au plus. Une coupure ne fait perdre que le morceau en cours.</p>

          <div className="pt-1 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">Ou lier une vidéo hébergée</div>
          <input
            type="text"
            value={lien.titre}
            onChange={(evenement) => setLien({ ...lien, titre: evenement.target.value })}
            placeholder={classe.libelle}
            aria-label="Titre du lien"
            className={CLASSE_CHAMP}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              type="url"
              inputMode="url"
              value={lien.url}
              onChange={(evenement) => setLien({ ...lien, url: evenement.target.value })}
              placeholder="https://…"
              aria-label="Adresse du replay"
              className={`${CLASSE_CHAMP} min-w-0 flex-1`}
            />
            <button
              type="button"
              disabled={!lien.url.trim() || gestes.lierReplay.isPending}
              onClick={() => void ajouterLien()}
              className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-60"
            >
              <Link2 className="h-4 w-4" aria-hidden />
              Ajouter le lien
            </button>
          </div>
        </div>
      )}

      <div className="px-4 py-3 text-[11px] text-codialis-gray sm:px-6">
        {conservation
          ? `Conservés jusqu’au ${dateCourte(conservation)} : six mois après la fin de formation des apprenants, qui y figurent.`
          : 'Conservés tant qu’aucune date de fin de formation n’est connue pour les apprenants de la classe.'}
        {retires.length > 0 && ` ${retires.length} replay${retires.length > 1 ? 's' : ''} retiré${retires.length > 1 ? 's' : ''}.`}
      </div>

      <Modale
        titre="Retirer ce replay ?"
        sousTitre="Le fichier est effacé du serveur ; la ligne garde la trace du retrait."
        ouverte={aRetirer !== null}
        onFermer={() => setARetirer(null)}
        largeur="max-w-md"
      >
        <div className="space-y-3 p-4 sm:p-6">
          <p className="text-sm text-codialis-navy">{aRetirer?.titre}</p>
          <input
            type="text"
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder="Motif (facultatif)"
            aria-label="Motif du retrait"
            className={CLASSE_CHAMP}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setARetirer(null)}
              className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={gestes.retirerReplay.isPending}
              onClick={() => void confirmerRetrait()}
              className="min-h-[44px] rounded-xl bg-red-600 px-4 text-sm font-semibold text-white disabled:opacity-60"
            >
              Retirer
            </button>
          </div>
        </div>
      </Modale>
    </Carte>
  );
}
